import React from 'react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ModalFooterAction {
  label: string;
  onClick?: () => void;
  type?: 'button' | 'submit';
  formId?: string;
  isLoading?: boolean;
  disabled?: boolean;
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost';
}

export interface ModalFooterProps {
  children?: React.ReactNode;
  primaryAction?: ModalFooterAction;
  secondaryAction?: ModalFooterAction;
  tertiaryAction?: ModalFooterAction;
  alignment?: 'left' | 'center' | 'right' | 'between';
  className?: string;
}

const alignmentClasses: Record<string, string> = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end',
  between: 'justify-between',
};

export const ModalFooter: React.FC<ModalFooterProps> = ({
  children,
  primaryAction,
  secondaryAction,
  tertiaryAction,
  alignment = 'right',
  className,
}) => {
  if (children) {
    return (
      <div
        className={cn(
          'flex-none border-t border-border/50 bg-background px-4 sm:px-6 py-3 sm:py-4',
          className
        )}
      >
        {children}
      </div>
    );
  }

  const handleClick = (action: ModalFooterAction) => (e: React.MouseEvent) => {
    if (action.isLoading) {
      e.preventDefault();
      return;
    }
    if (action.onClick) {
      action.onClick();
    }
  };

  return (
    <div
      className={cn(
        'flex-none border-t border-border/50 bg-background px-4 sm:px-6 py-3 sm:py-4',
        className
      )}
    >
      <div
        className={cn(
          'flex flex-row gap-3',
          alignmentClasses[alignment]
        )}
      >
        {/* Tertiary action (left side) */}
        {tertiaryAction && (
          <Button
            type={tertiaryAction.type || 'button'}
            variant={tertiaryAction.variant || 'ghost'}
            onClick={handleClick(tertiaryAction)}
            disabled={tertiaryAction.disabled || tertiaryAction.isLoading}
            className="flex-1 sm:flex-initial sm:mr-auto rounded-xl"
          >
            {tertiaryAction.label}
          </Button>
        )}

        {/* Secondary action (cancel) */}
        {secondaryAction && (
          <Button
            type={secondaryAction.type || 'button'}
            variant={secondaryAction.variant || 'outline'}
            onClick={handleClick(secondaryAction)}
            disabled={secondaryAction.disabled || secondaryAction.isLoading}
            className="flex-1 rounded-xl"
          >
            {secondaryAction.label}
          </Button>
        )}

        {/* Primary action (submit) */}
        {primaryAction && (
          <Button
            type={primaryAction.type || 'submit'}
            variant={primaryAction.variant || 'default'}
            form={primaryAction.formId}
            onClick={handleClick(primaryAction)}
            disabled={primaryAction.disabled || primaryAction.isLoading}
            className="flex-1 rounded-xl"
          >
            {primaryAction.isLoading && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            {primaryAction.isLoading ? 'Processando...' : primaryAction.label}
          </Button>
        )}
      </div>
    </div>
  );
};
